/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { RefObject } from 'preact';
import { useCallback, useRef } from 'preact/hooks';
import { stringLiteralArray } from '../../utils/UNSAFE_arrayUtils';
import { Keys, containsKey, isKeyDefined, addKey, removeKey } from '../../utils/UNSAFE_keys';
import { SelectionDetail, SelectionMode } from '../../UNSAFE_Collection';
import { useUser } from '../UNSAFE_useUser';
import { isMobile } from '../../utils/PRIVATE_collectionUtils';

/**
 * Type for selection behavior.  See useMultipleSelection hook for details.
 */
export const behaviors = stringLiteralArray(['toggle', 'replace']);
export type SelectionBehavior = typeof behaviors[number];

/**
 * Type for payload of selection range change event handler
 */
export type SelectionRangeDetail<K> = {
  value: { start: K; end: K };
};

const isShiftModeSupported = <K>(
  anchorKey: K,
  selectedKeys: Keys<K>,
  selectionMode: SelectionMode,
  selectionBehavior: SelectionBehavior
) => {
  return (
    selectionMode === 'multiple' &&
    selectionBehavior !== 'toggle' &&
    containsKey(selectedKeys, anchorKey)
  );
};

const handleSelect = <K>(
  event: MouseEvent | KeyboardEvent,
  keyExtractor: (element: HTMLElement) => K | null,
  selectedKeys: Keys<K>,
  selectionMode: SelectionMode,
  selectionBehavior: SelectionBehavior,
  isSelectionRequired: boolean,
  keyboardCursorRef: RefObject<K | undefined>,
  anchorKey?: K,
  onChange?: (detail: SelectionDetail<K>) => void,
  onRangeChange?: (detail: SelectionRangeDetail<K>) => void
) => {
  const itemKey = keyExtractor(event.target as HTMLElement);
  if (itemKey == null) {
    return;
  }

  // handle shift key selection
  if (
    event.shiftKey &&
    anchorKey !== undefined &&
    onRangeChange &&
    isShiftModeSupported(anchorKey, selectedKeys, selectionMode, selectionBehavior)
  ) {
    onRangeChange({ value: { start: anchorKey, end: itemKey } });
    // set keyboard selection cursor in case user extends with SHIFT+ARROW
    if (keyboardCursorRef) {
      keyboardCursorRef.current = itemKey;
    }
    return;
  }

  const selected = containsKey(selectedKeys, itemKey);
  let keySet = selectedKeys;
  if (!selected) {
    if (
      selectionMode === 'single' ||
      (event as KeyboardEvent).key === 'Enter' ||
      (selectionBehavior === 'replace' && !(event.ctrlKey || event.metaKey))
    ) {
      // should only contains this item key
      keySet = { all: false, keys: new Set([itemKey]) };
    } else {
      // add to existing selected keys
      keySet = addKey(selectedKeys, itemKey);
    }
  } else if ((event as KeyboardEvent).key !== 'Enter') {
    // no de-select for enter key
    if (
      selectionBehavior === 'toggle' ||
      event.ctrlKey ||
      event.metaKey ||
      (event as KeyboardEvent).key === ' '
    ) {
      // de-select only when ctrl/meta key is pressed or selectionBehavior is
      // explicitly set to toggle
      keySet = removeKey(selectedKeys, itemKey, isSelectionRequired);
    } else if (selectionMode === 'multiple' && (selectedKeys.all || selectedKeys.keys.size > 1)) {
      // in this case everything should be de-selected except for this key
      keySet = { all: false, keys: new Set([itemKey]) };
    }
  }

  // only invoke callback if there is really a change
  // note onSelectionChange should never be null here
  if (selectedKeys != keySet && onChange) {
    onChange({ value: keySet, target: event.target });
    if (event.type === 'click') {
      event.stopPropagation();
    }
  }
};

/**
 * Hook for handling various selection mode.  This can be reuse by all Collection components.
 * @param keyExtractor function to extract the key based on provided element
 * @param selectedKeys the currently selected keys
 * @param selectionMode the selection mode
 * @param isSelectionRequired true if empty selected key is not allowed, false otherwise
 * @param selectionBehavior the selection behavior.  If the value is 'toggle', then the key is
 *                          added/removed from the current selected keys.  If the value is 'replace',
 *                          then the current selected keys are cleared first before the key is added
 *                          unless the ctrl/shift/meta key is pressed.
 * @param allowEnterKey whether enter key can be use to change selection.
 * @param onChange callback function that is invoked when selection has changed.
 * @param anchorKey the anchor key.  Usually this is the same as current key except when shift key is pressed.
 * @param currentKey the current key
 * @param getVerticalPrevNextKey function to get the previous/next key in the vertical direction based on the current key
 * @param getHorizontalPrevNextKey function to get the previous/next key in the horizontal direction based on the current key
 * @param scrollToKey function to scroll the item with the key to be visible
 * @param onRangeChange callback function that is invoked when the selection range has changed.
 * @returns an event map that the consumer can register as event listeners
 */
export function useSelection<K>(
  keyExtractor: (element: HTMLElement) => K | null,
  selectedKeys: Keys<K>,
  selectionMode: SelectionMode,
  isSelectionRequired: boolean,
  selectionBehavior: SelectionBehavior,
  allowEnterKey: boolean,
  onChange?: (detail: SelectionDetail<K>) => void,
  anchorKey?: K,
  currentKey?: K | undefined,
  getVerticalPrevNextKey?: (currentKey: K | undefined, isPrev: boolean) => () => K | null,
  getHorizontalPrevNextKey?: (currentKey: K | undefined, isPrev: boolean) => () => K | null,
  scrollToKey?: (key: K) => void,
  onRangeChange?: (detail: SelectionRangeDetail<K>) => void
) {
  // ref to track the cursor for the SHIFT+ARROW keyboard selection separately than the anchor of the selection.
  const keyboardSelectionCursor = useRef<K>();

  const onClick = useCallback(
    (event: MouseEvent) => {
      handleSelect(
        event,
        keyExtractor,
        selectedKeys,
        selectionMode,
        isMobile() ? 'toggle' : selectionBehavior,
        isSelectionRequired,
        keyboardSelectionCursor,
        anchorKey,
        onChange,
        onRangeChange
      );
      if (!event.shiftKey) {
        // reset keyboard cursor when using mouse to select
        keyboardSelectionCursor.current = undefined;
      }
    },
    [
      keyExtractor,
      selectedKeys,
      selectionMode,
      selectionBehavior,
      isSelectionRequired,
      keyboardSelectionCursor,
      anchorKey,
      onChange,
      onRangeChange
    ]
  );

  // general keyboard events listen to keyUp for a11y
  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      // older browser that we don't support returns 'Spacebar' for space key
      if (event.key === ' ' || (event.key === 'Enter' && allowEnterKey)) {
        handleSelect(
          event,
          keyExtractor,
          selectedKeys,
          selectionMode,
          selectionBehavior,
          isSelectionRequired,
          keyboardSelectionCursor,
          anchorKey,
          onChange,
          event.shiftKey && event.key === ' ' ? onRangeChange : undefined
        );
      }
    },
    [
      keyExtractor,
      selectedKeys,
      selectionMode,
      selectionBehavior,
      isSelectionRequired,
      anchorKey,
      onChange,
      onRangeChange
    ]
  );

  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  // arrowKey events listen to keyDown so they can support holding them down
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // prevent default (propagation) for keyDown to space bar causing container to scroll
      if (event.key === ' ') {
        event.preventDefault();
        return;
      }

      const key = keyboardSelectionCursor.current || currentKey;
      const keyHandlers: Record<string, (() => K | null) | undefined> = {
        ArrowUp: getVerticalPrevNextKey?.(key, true),
        ArrowDown: getVerticalPrevNextKey?.(key, false),
        ArrowLeft: getHorizontalPrevNextKey?.(key, !isRtl),
        ArrowRight: getHorizontalPrevNextKey?.(key, isRtl)
      };
      const isBothDirections = getVerticalPrevNextKey && getHorizontalPrevNextKey;
      if (Object.keys(keyHandlers).includes(event.key)) {
        if (event.shiftKey && scrollToKey && selectionMode === 'multiple') {
          let keySet = selectedKeys;
          // on first selection event, set keyboard cursor to start at currentKey
          if (!keyboardSelectionCursor.current && isKeyDefined(currentKey)) {
            keySet = { all: false, keys: new Set([currentKey as K]) };
          }
          const newAnchorKey = keyHandlers[event.key];
          const cursorKey = newAnchorKey?.();
          if (cursorKey != null) {
            // keep visible
            scrollToKey(cursorKey);

            // if in 2D, use range selection to extend or subtract
            if (isBothDirections) {
              if (onRangeChange && currentKey != null) {
                onRangeChange({ value: { start: currentKey, end: cursorKey } });
              }
            } else {
              // extend selection
              if (!containsKey(keySet, cursorKey)) {
                keySet = addKey(keySet, cursorKey);
                // subtract from selection
              } else if (
                keyboardSelectionCursor.current != null &&
                keyboardSelectionCursor.current !== cursorKey
              ) {
                keySet = removeKey(keySet, keyboardSelectionCursor.current, isSelectionRequired);
              }
              if (selectedKeys != keySet && onChange) {
                onChange({ value: keySet, target: event.target });
              }
            }
            // update selection cursor
            keyboardSelectionCursor.current = cursorKey;
          }
        } else {
          // reset keyBoard on non-shifted arrow use as this interrupts the extend mode (and would make for messy logic that doesn't align with current Jet behavior)
          keyboardSelectionCursor.current = undefined;
        }
      }
    },
    [
      selectedKeys,
      isSelectionRequired,
      onChange,
      currentKey,
      getVerticalPrevNextKey,
      getHorizontalPrevNextKey,
      scrollToKey
    ]
  );

  const selectionProps =
    selectionMode === 'none' || onChange == null ? {} : { onClick, onKeyDown, onKeyUp };
  return { selectionProps };
}
